import { DateFormatter, fromDate, getLocalTimeZone } from '@internationalized/date';

const timeZone = getLocalTimeZone();

const customFormatter = (options?: Intl.DateTimeFormatOptions): DateFormatter =>
  new DateFormatter('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
    ...options,
  });

const formatter = customFormatter();

const shortFormatter = customFormatter({
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone,
});

const dateToCalendarDateTime = (date: Date) => fromDate(date, timeZone);

type DateInput = Date | string | number;

const toDate = (value: DateInput): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export type DateFormatPresetId = 'short' | 'medium' | 'long';

export type DateFormatPreset = {
  id: DateFormatPresetId;
  label: string;
  description: string;
  options: Intl.DateTimeFormatOptions;
  formatter: DateFormatter;
  format: (value: DateInput) => string;
};

const dateFormatPresetDefinitions: Array<{
  id: DateFormatPresetId;
  label: string;
  description: string;
  options: Intl.DateTimeFormatOptions;
}> = [
  {
    id: 'short',
    label: 'Short',
    description: 'Compact numeric date and time (e.g., 12/31/24, 5:00 PM in en-US; actual format may vary by locale)',
    options: {
      dateStyle: 'short',
      timeStyle: 'short',
    },
  },
  {
    id: 'medium',
    label: 'Medium',
    description: 'Readable month abbreviation with day, year, and time (e.g., Dec 31, 2024, 5:00 PM)',
    options: {
      dateStyle: 'medium',
      timeStyle: 'short',
    },
  },
  {
    id: 'long',
    label: 'Long',
    description: 'Detailed weekday, month, day, year, and time (e.g., Tuesday, December 31, 2024 at 5:00 PM)',
    options: {
      dateStyle: 'full',
      timeStyle: 'short',
    },
  },
];

const dateFormatPresets: DateFormatPreset[] = dateFormatPresetDefinitions.map(({ id, label, description, options }) => {
  const formatterInstance = customFormatter({
    ...options,
    timeZone,
  });

  return {
    id,
    label,
    description,
    options: { ...options },
    formatter: formatterInstance,
    format: (value: DateInput) => {
      const date = toDate(value);

      return date ? formatterInstance.format(date) : '';
    },
  };
});

const defaultDateFormatPresetId: DateFormatPresetId = 'medium';

const getDateFormatPreset = (presetId: DateFormatPresetId): DateFormatPreset => {
  const preset = dateFormatPresets.find(({ id }) => id === presetId);

  if (preset) {
    return preset;
  }

  const fallback = dateFormatPresets.find(({ id }) => id === defaultDateFormatPresetId);

  return fallback ?? dateFormatPresets[0];
};

const resolveDateFormatPreset = (presetId?: DateFormatPresetId | null): DateFormatPreset =>
  getDateFormatPreset(presetId ?? defaultDateFormatPresetId);

const formatDateWithPreset = (value: DateInput, presetId?: DateFormatPresetId | null): string => {
  const preset = resolveDateFormatPreset(presetId);

  return preset.format(value);
};

/**
 * Comparator for AG Grid date columns.
 * Compares two date strings (ISO or parseable) for sorting.
 */
export function dateComparator(valueA?: string, valueB?: string): number {
  const date1 = typeof valueA === 'string' ? Date.parse(valueA) : Number.NaN;
  const date2 = typeof valueB === 'string' ? Date.parse(valueB) : Number.NaN;

  const isDate1Valid = !Number.isNaN(date1);
  const isDate2Valid = !Number.isNaN(date2);

  if (!isDate1Valid && !isDate2Valid) return 0;
  if (!isDate1Valid) return -1;
  if (!isDate2Valid) return 1;

  return date1 - date2;
}

export {
  customFormatter,
  dateFormatPresets,
  dateToCalendarDateTime,
  defaultDateFormatPresetId,
  formatter,
  formatDateWithPreset,
  getDateFormatPreset,
  resolveDateFormatPreset,
  shortFormatter,
  timeZone,
};
